import React from 'react';
import {
  parseAddress,
  formatRelativeDate,
  formatScheduledWhen,
  getPreviewText,
  getInitials,
  shortenEmail,
  getOutgoingMailbox,
  getScheduledSendAt,
} from '../utils/emailParsing';

/**
 * MailListItem — a single Apple-Mail / Gmail style preview row.
 *
 * Presentational only. Clicking selects the email (handled by parent). Shows
 * sender/recipient, subject, a one-line preview, time, unread dot and a few
 * lightweight badges (spam / replied / enthusiasm / tags).
 */
export default function MailListItem({ email, isActive, isOutgoing, onSelect, isBlockedSender }) {
  const personField = isOutgoing
    ? (email.to || email.recipientEmail || email.recipients || '')
    : (email.from || email.senderEmail || '');
  const person = parseAddress(personField);
  const isBlocked = typeof isBlockedSender === 'function' && isBlockedSender(personField);
  const businessName = (email.businessName || email.business_name || '').trim();
  const displayName = businessName || person.name || person.email || 'Unknown';

  const unread = !isOutgoing && email.isRead === false && email.isDeleted !== true;
  const preview = getPreviewText(email);
  const isScheduled = email.isScheduled === true;
  const scheduledAt = isScheduled ? getScheduledSendAt(email) : null;
  const time = isScheduled
    ? formatScheduledWhen(scheduledAt)
    : formatRelativeDate(email.timestamp || email.date);

  const enthusiasm = typeof email.enthusiasmScore === 'number' ? email.enthusiasmScore : null;
  const tags = email.tags
    ? (Array.isArray(email.tags) ? email.tags : Object.values(email.tags))
    : [];

  // Incoming: the mailbox it landed in. Scheduled: the account it will send from.
  const mailbox = isScheduled ? getOutgoingMailbox(email) : (email.accountEmail || '');
  const showMailbox = (!isOutgoing || isScheduled) && !!mailbox;
  const isBounce = email.isBounce === true || email.isBounceMail === true;
  // Inbox Reply has an answer waiting for this mail (see the banner above
  // the list). Once answered the draft is gone, so a replied mail never flags.
  const hasAgentDraft = !isOutgoing
    && email.hasDraft === true
    && email.agentAction === 'draft_created'
    && !email.isReplied;
  const hasBadges = email.isSpam || isBounce || enthusiasm !== null || tags.length > 0;

  return (
    <button
      type="button"
      className={`ivx-item ${isActive ? 'active' : ''} ${unread ? 'unread' : ''}`}
      onClick={() => onSelect(email)}
    >
      <div className="ivx-item-unread-dot" aria-hidden="true" />

      <div className="ivx-item-avatar">
        {getInitials(displayName)}
        {isBlocked ? (
          <span className="ivx-item-blocked-x" title="Blocked — can no longer be emailed" aria-label="Blocked">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : email.isLeadMail && (
          <span className="ivx-item-lead-check" title="Saved as a lead" aria-label="Lead">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </div>

      <div className="ivx-item-main">
        <div className="ivx-item-top">
          <span className="ivx-item-name">{displayName}</span>
          <span className={`ivx-item-time${isScheduled && time ? ' scheduled' : ''}`}>{time}</span>
        </div>
        <div className="ivx-item-subject">
          {email.isReplied && (
            <svg className="ivx-item-replied-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-label="Replied">
              <path d="M10 9V5l-7 7 7 7v-4c5 0 8 1.5 10 4-1-5-4-9-10-10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          )}
          {hasAgentDraft && (
            <svg className="ivx-item-draft-icon" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-label="Reply drafted">
              <path d="M12 2.5l1.85 5.15L19 9.5l-5.15 1.85L12 16.5l-1.85-5.15L5 9.5l5.15-1.85z" />
            </svg>
          )}
          {email.subject || '(No subject)'}
        </div>
        <div className="ivx-item-preview">{preview || '\u00A0'}</div>

        {(showMailbox || hasBadges) && (
          <div className="ivx-item-meta">
            {(showMailbox || email.isSpam || isBounce) && (
              <div className="ivx-item-mailrow">
                {showMailbox && (
                  <span
                    className="ivx-mailbox-chip"
                    title={isScheduled ? `Sends from ${mailbox}` : `Received at ${mailbox}`}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path d="M3 7l9 6 9-6M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {shortenEmail(mailbox, 22)}
                  </span>
                )}
                {email.isSpam && <span className="ivx-badge ivx-badge-spam">Spam</span>}
                {isBounce && (
                  <span className={`ivx-badge ivx-badge-bounce ${email.bounceType === 'hard' ? 'hard' : ''}`}>
                    {email.bounceType === 'hard' ? 'Undeliverable' : 'Bounce'}
                  </span>
                )}
              </div>
            )}

            {(tags.length > 0 || enthusiasm !== null) && (
              <div className="ivx-item-tagrow">
                {tags.slice(0, 2).map((t, i) => {
                  const tag = typeof t === 'string' ? { name: t } : t;
                  return (
                    <span
                      key={i}
                      className="ivx-badge ivx-badge-tag"
                      style={tag.color ? { backgroundColor: tag.color, color: '#fff', borderColor: 'transparent' } : undefined}
                    >
                      {tag.name || tag.tag}
                    </span>
                  );
                })}
                {enthusiasm !== null && (
                  <span className={`ivx-badge ivx-badge-enth ${enthusiasm >= 5 ? 'high' : enthusiasm <= 2 ? 'low' : ''}`}>
                    {enthusiasm}/10
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
